import React, { useState, useRef, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import { useLocation } from "react-router-dom";
import {
  fetchUserData,
  UploadLeadFileService,
  DownloadLeadTemplateService,
} from "../Services/APIService";

interface User {
  userId: number;
  userName: string;
  userTypeId: number;
  isActive: boolean;
}

interface RowResult {
  row: number;
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  status: "Success" | "Duplicate" | "Error";
  reason: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  totalRows: number;
  imported: number;
  duplicates: number;
  skipped: number;
  errorCount: number;
  errors: string[];
  rowResults: RowResult[];

  resultExcel?: string;   // 👈 Add this line
}

const ImportLead: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [users, setUsers]               = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("");
  const [isDragging, setIsDragging]     = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading]   = useState(false);
  const [uploadDone, setUploadDone]     = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const location     = useLocation();

  const tenantId  = parseInt(localStorage.getItem("tenantId") ?? "0", 10);
  const createdBy = parseInt(localStorage.getItem("UserId")   ?? "0", 10);

  useEffect(() => {
    const menuID = location.state?.menuID;
    const previousMenuID = localStorage.getItem("activeMenuID");
    if (menuID !== previousMenuID || menuID === null) {
      localStorage.setItem("activeMenuID", menuID);
    } else {
      localStorage.removeItem("activeMenuID");
    }
  }, [location]);

  useEffect(() => {
    (async () => {
      try {
        setUsersLoading(true);
        const userRes = await fetchUserData();
        setUsers(userRes.filter((u: User) => u.isActive));
      } catch {
        toast.error("Failed to load users.");
      } finally {
        setUsersLoading(false);
      }
    })();
  }, []);

  const toggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".xlsx")) {
      setUploadedFile(file); setUploadDone(false); setUploadProgress(0); setUploadResult(null);
    } else if (file) toast.error("Only .xlsx files are accepted.");
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith(".xlsx")) {
      setUploadedFile(file); setUploadDone(false); setUploadProgress(0); setUploadResult(null);
    } else if (file) toast.error("Only .xlsx files are accepted.");
  };

  const handleImport = async () => {
    if (!uploadedFile || !selectedUser) return;
    setIsUploading(true); setUploadProgress(0); setUploadResult(null);
    let progress = 0;
    const ticker = setInterval(() => {
      progress += Math.random() * 8 + 2;
      if (progress < 85) setUploadProgress(Math.floor(progress));
    }, 200);
    try {
      const result = await UploadLeadFileService(uploadedFile, parseInt(selectedUser), createdBy, tenantId);
      clearInterval(ticker);
      setUploadProgress(100); setUploadResult(result); setUploadDone(true);
      if (result.errorCount > 0) {
        toast.success(`Imported ${result.imported} leads. ${result.errorCount} row(s) had errors.`);
      } else if (result.duplicates > 0) {
        toast.success(`Imported ${result.imported} leads. ${result.duplicates} duplicate(s) skipped.`);
      } else {
        toast.success(`Successfully imported ${result.imported} leads!`);
      }
    } catch (err: any) {
      clearInterval(ticker); setUploadProgress(0);
      toast.error(err?.message ?? "Upload failed.");
    } finally { setIsUploading(false); }
  };

  const handleReset = () => {
    setUploadedFile(null); setUploadProgress(0); setUploadDone(false);
    setUploadResult(null); setSelectedUser("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = async () => {
    try { await DownloadLeadTemplateService(); toast.success("Template downloaded!"); }
    catch { toast.error("Failed to download template."); }
  };

 const downloadReport = () => {
  if (!uploadResult?.resultExcel) {
    toast.error("No report available");
    return;
  }

  const byteCharacters = atob(uploadResult.resultExcel);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);

  const fileName = `lead_import_report_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  link.download = fileName;
  link.click();

  toast.success("Report downloaded!");
};

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const selectedUserObj = users.find((u) => u.userId === parseInt(selectedUser));
  const getInitials = (name: string) =>
    name.split(" ").map((w) => w[0] ?? "").slice(0, 2).join("").toUpperCase();

  return (
    <div className="wrapper">
      <Toaster position="top-center" reverseOrder={false} containerStyle={{ zIndex: 10000 }} />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

                .il-wrap {
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  display: flex;
                  flex-direction: column;
                  gap: 14px;
                  height: 100%;
                }

                .il-card {
                  background: #fff;
                  border-radius: 6px;
                  border: 1px solid #e8e8f0;
                  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
                  overflow: hidden;
                }

                .il-card-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 22px 18px;
                  border-bottom: 1px solid #f0f0f8;
                  background: #fff;
                  flex-wrap: wrap;
                  gap: 10px;
                }

                .il-card-header-title {
                  font-size: 14px;
                  font-weight: 700;
                  color: #2d2d4e;
                  margin: 0;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                }

                .il-card-header-title-dot {
                  width: 8px; height: 8px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, #7b5ea7, #5b4fcf);
                  flex-shrink: 0;
                }

                .il-card-header-right {
                  display: flex;
                  align-items: center;
                  gap: 10px;
                }

                .il-step-pills {
                  display: flex;
                  align-items: center;
                  gap: 6px;
                }

                .il-pill {
                  display: flex;
                  align-items: center;
                  gap: 5px;
                  padding: 4px 10px;
                  border-radius: 20px;
                  font-size: 11px;
                  font-weight: 700;
                  border: 1px solid;
                }

                .il-pill.idle   { background: #f5f4fc; color: #b0aac8; border-color: #e4e0f4; }
                .il-pill.active { background: #ece9f8; color: #7b5ea7;  border-color: #c8c0e8; }
                .il-pill.done   { background: #e8faf0; color: #27ae60;  border-color: #b8ead0; }

                .il-pill-num {
                  width: 16px; height: 16px;
                  border-radius: 50%;
                  display: flex; align-items: center; justify-content: center;
                  font-size: 9px; font-weight: 800;
                  flex-shrink: 0;
                }

                .il-pill.idle   .il-pill-num { background: #e4e0f4; color: #b0aac8; }
                .il-pill.active .il-pill-num { background: #7b5ea7; color: #fff; }
                .il-pill.done   .il-pill-num { background: #27ae60; color: #fff; }

                .il-pill-arrow { color: #ccc; font-size: 10px; }

                .il-card-body {
                  padding: 16px 18px;
                }

                .il-grid {
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 14px;
                }
                @media(max-width:768px){ .il-grid{ grid-template-columns:1fr; } }

                .il-label {
                  font-size: 11px; font-weight: 700; color: #9090a8;
                  letter-spacing: 0.6px; text-transform: uppercase;
                  margin-bottom: 6px; display: block;
                }

                .il-sel-wrap { position: relative; }
                .il-sel {
                  width: 100%; appearance: none;
                  background: #faf9ff; border: 1px solid #d8d8e8;
                  border-radius: 6px; padding: 9px 36px 9px 12px;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  font-size: 13px; font-weight: 500; color: #2d2d4e;
                  cursor: pointer; outline: none; transition: all 0.2s;
                }
                .il-sel:focus { border-color: #7b5ea7; box-shadow: 0 0 0 2px rgba(123,94,167,0.12); }
                .il-sel:disabled { opacity: 0.6; cursor: not-allowed; }
                .il-sel-arrow { position: absolute; right: 11px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #9090a8; font-size: 12px; }

                .il-user-prev {
                  margin-top: 10px; padding: 10px 12px;
                  background: #f7f5fc; border-radius: 8px;
                  border: 1px solid #e4e0f4;
                  display: flex; align-items: center; gap: 10px;
                  animation: fadeUp 0.25s ease;
                }
                @keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }

                .il-avatar {
                  width: 34px; height: 34px; border-radius: 8px;
                  background: linear-gradient(135deg,#7b5ea7,#5b4fcf);
                  color: #fff; display: flex; align-items: center; justify-content: center;
                  font-weight: 800; font-size: 12px; flex-shrink: 0;
                }
                .il-uname { font-size: 13px; font-weight: 700; color: #2d2d4e; }
                .il-urole { font-size: 11px; color: #a0a0b8; }
                .il-check {
                  margin-left: auto; width: 22px; height: 22px; border-radius: 50%;
                  background: linear-gradient(135deg,#43c98c,#2ecc71);
                  color: #fff; display: flex; align-items: center; justify-content: center;
                  font-size: 11px; flex-shrink: 0;
                }

                .il-empty-user {
                  margin-top: 10px; padding: 58px 12px;
                  background: #faf9ff; border-radius: 8px;
                  border: 1px dashed #d8d2f0; text-align: center;
                }

                .il-skeleton {
                  height: 38px;
                  background: linear-gradient(90deg,#f0eef8 25%,#e8e4f4 50%,#f0eef8 75%);
                  background-size: 200% 100%; border-radius: 6px;
                  animation: skelShimmer 1.4s infinite;
                }
                @keyframes skelShimmer { to{background-position:-200% 0} }

                .il-hint {
                  display: flex; align-items: center; gap: 6px;
                  padding: 9px 11px; background: #faf9ff;
                  border-radius: 6px; border: 1px solid #e4e0f4;
                  margin-bottom: 10px;
                }
                .il-hint-txt { font-size: 11px; color: #a0a0b8; }
                .il-hint-link {
                  color: #7b5ea7; font-weight: 700; cursor: pointer;
                  font-size: 11px; background: none; border: none; padding: 0;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                }
                .il-hint-link:hover { text-decoration: underline; }

                .il-dz {
                  border: 2px dashed #d0cae8; border-radius: 8px;
                  padding: 20px 16px; text-align: center;
                  cursor: pointer; transition: all 0.25s; background: #faf9ff;
                }
                .il-dz:hover, .il-dz.dragging { border-color: #7b5ea7; background: #f0eeff; }
                .il-dz.has-file { border-style: solid; border-color: #7b5ea7; background: #f0eeff; }
                .il-dz-ico { font-size: 28px; margin-bottom: 6px; display: block; transition: transform 0.25s; }
                .il-dz:hover .il-dz-ico { transform: translateY(-3px); }
                .il-dz-ttl { font-size: 13px; font-weight: 700; color: #2d2d4e; margin-bottom: 2px; }
                .il-dz-sub { font-size: 11px; color: #b0aac8; margin-bottom: 10px; }
                .il-browse {
                  display: inline-flex; align-items: center; gap: 5px;
                  background: linear-gradient(135deg,#7b5ea7,#5b4fcf); color: #fff;
                  border: none; border-radius: 6px; padding: 7px 16px;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  font-size: 11px; font-weight: 700; cursor: pointer;
                  box-shadow: 0 3px 10px rgba(91,79,207,.25); transition: all 0.2s;
                }
                .il-browse:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(91,79,207,.35); }
                .il-fmt-tag {
                  display: inline-block; margin-top: 8px;
                  background: #ece9f8; color: #7b5ea7;
                  font-size: 10px; font-weight: 700; letter-spacing: 1px;
                  text-transform: uppercase; padding: 2px 8px; border-radius: 4px;
                }

                .il-fstrip {
                  display: flex; align-items: center; gap: 10px;
                  padding: 9px 12px; background: #fff;
                  border-radius: 8px; border: 1px solid #e0daf0;
                  margin-top: 10px; animation: fadeUp 0.25s ease;
                }
                .il-fico {
                  width: 34px; height: 34px; border-radius: 7px;
                  background: linear-gradient(135deg,#1e7e34,#27ae60);
                  display: flex; align-items: center; justify-content: center;
                  font-size: 16px; flex-shrink: 0;
                }
                .il-fname { font-size: 12px; font-weight: 700; color: #2d2d4e; }
                .il-fsize { font-size: 11px; color: #b0aac8; }
                .il-fremove {
                  margin-left: auto; background: #fff0f0; border: none;
                  color: #e74c3c; width: 26px; height: 26px; border-radius: 6px;
                  cursor: pointer; font-size: 12px;
                  display: flex; align-items: center; justify-content: center;
                  transition: all 0.2s; flex-shrink: 0;
                }
                .il-fremove:hover { background: #e74c3c; color: #fff; }

                .il-prog { margin-top: 10px; animation: fadeUp 0.25s ease; }
                .il-prog-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
                .il-prog-lbl { font-size: 11px; font-weight: 600; color: #7b5ea7; }
                .il-prog-pct { font-size: 11px; font-weight: 800; color: #5b4fcf; }
                .il-prog-track { height: 6px; background: #ece9f8; border-radius: 99px; overflow: hidden; }
                .il-prog-fill {
                  height: 100%;
                  background: linear-gradient(90deg,#7b5ea7,#5b4fcf,#8b6ff7);
                  border-radius: 99px; transition: width 0.15s ease;
                  position: relative; overflow: hidden;
                }
                .il-prog-fill::after {
                  content:''; position:absolute; top:0; left:-100%;
                  width:100%; height:100%;
                  background:linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent);
                  animation:shimmer 1.2s infinite;
                }
                @keyframes shimmer { to{left:100%} }

                /* ── Success panel ── */
                .il-success { text-align: center; padding: 10px 16px; animation: fadeUp 0.3s ease; }
                .il-success-ring {
                  width: 48px; height: 48px; border-radius: 50%;
                  background: linear-gradient(135deg,#43c98c,#2ecc71);
                  display: flex; align-items: center; justify-content: center;
                  font-size: 22px; margin: 0 auto 10px;
                  box-shadow: 0 6px 18px rgba(46,204,113,.28);
                }
                .il-success-ttl { font-size: 15px; font-weight: 800; color: #2d2d4e; margin-bottom: 4px; }
                .il-success-sub { font-size: 11px; color: #a0a0b8; }

                /* ── Stats row ── */
                .il-result-stats {
                  display: flex; gap: 8px; justify-content: center;
                  margin-top: 10px; flex-wrap: wrap;
                }
                .il-stat {
                  padding: 5px 12px; border-radius: 6px;
                  font-size: 11px; font-weight: 700;
                }
                .il-stat-imported  { background: #e8faf0; color: #27ae60; }
                .il-stat-duplicate { background: #fff3cd; color: #856404; }
                .il-stat-skipped   { background: #fff7e6; color: #e67e22; }
                .il-stat-errors    { background: #ffeef0; color: #e74c3c; }

                .il-errors {
                  margin-top: 8px; background: #ffeef0; border-radius: 6px;
                  padding: 8px 12px; max-height: 80px; overflow-y: auto;
                }
                .il-error-item {
                  font-size: 10px; color: #c0392b; padding: 2px 0;
                  border-bottom: 1px solid rgba(192,57,43,0.1);
                }
                .il-error-item:last-child { border-bottom: none; }

                /* ── Download report button ── */
                .il-btn-download {
                  display: inline-flex; align-items: center; gap: 6px;
                  margin-top: 12px;
                  padding: 8px 20px;
                  background: linear-gradient(135deg,#1e7e34,#27ae60);
                  border: none; border-radius: 6px;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  font-size: 12px; font-weight: 700; color: #fff;
                  cursor: pointer; transition: all 0.2s;
                  box-shadow: 0 3px 10px rgba(39,174,96,.28);
                }
                .il-btn-download:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 5px 14px rgba(39,174,96,.38);
                }

                /* ── Card footer ── */
                .il-card-footer {
                  display: flex; justify-content: flex-end; gap: 8px;
                  padding: 10px 18px;
                  border-top: 1px solid #f0f0f8;
                  background: #faf9ff;
                }

                .il-btn-reset {
                  padding: 8px 20px; background: transparent;
                  border: 1px solid #d8d8e8; border-radius: 6px;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  font-size: 12px; font-weight: 700; color: #9090a8;
                  cursor: pointer; transition: all 0.2s;
                }
                .il-btn-reset:hover { border-color: #b0aac8; color: #7b5ea7; }

                .il-btn-import {
                  padding: 8px 22px;
                  background: linear-gradient(135deg,#7b5ea7,#5b4fcf);
                  border: none; border-radius: 6px;
                  font-family: 'Plus Jakarta Sans', sans-serif;
                  font-size: 12px; font-weight: 700; color: #fff;
                  cursor: pointer; transition: all 0.2s;
                  box-shadow: 0 3px 10px rgba(91,79,207,.28);
                  display: flex; align-items: center; gap: 6px;
                }
                .il-btn-import:hover { transform: translateY(-1px); box-shadow: 0 5px 16px rgba(91,79,207,.38); }
                .il-btn-import:disabled { opacity: 0.42; cursor: not-allowed; transform: none; box-shadow: none; }

                .il-section-label {
                  font-size: 11px; font-weight: 700; color: #9090a8;
                  text-transform: uppercase; letter-spacing: 0.6px;
                  margin-bottom: 8px; display: flex; align-items: center; gap: 6px;
                }
                .il-section-label::after {
                  content: ''; flex: 1; height: 1px; background: #f0f0f8;
                }
              `}</style>

              <div className="il-wrap">

                {/* ── Main Card ── */}
                <div className="il-card">

                  {/* Card Header */}
                  <div className="il-card-header">
                    <div className="il-card-header-title">
                      <div className="il-card-header-title-dot" />
                      Import Lead
                    </div>

                    <div className="il-card-header-right">
                      <div className="il-step-pills">
                        <div className={`il-pill ${selectedUser ? "done" : "active"}`}>
                          <div className="il-pill-num">{selectedUser ? "✓" : "1"}</div>
                          Assign User
                        </div>
                        <span className="il-pill-arrow">›</span>
                        <div className={`il-pill ${uploadDone ? "done" : uploadedFile ? "active" : "idle"}`}>
                          <div className="il-pill-num">{uploadDone ? "✓" : "2"}</div>
                          Upload File
                        </div>
                        <span className="il-pill-arrow">›</span>
                        <div className={`il-pill ${uploadDone ? "done" : "idle"}`}>
                          <div className="il-pill-num">{uploadDone ? "✓" : "3"}</div>
                          Import
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="il-card-body">
                    <div className="il-grid">

                      {/* LEFT — Assign User */}
                      <div>
                        <div className="il-section-label">Assign to User</div>

                        {usersLoading ? (
                          <div className="il-skeleton" />
                        ) : (
                          <div className="il-sel-wrap">
                            <select
                              className="il-sel"
                              value={selectedUser}
                              onChange={(e) => setSelectedUser(e.target.value)}
                              disabled={usersLoading}
                            >
                              <option value="">— Select a user —</option>
                              {users.map((u) => (
                                <option key={u.userId} value={u.userId}>
                                  {u.userName}
                                </option>
                              ))}
                            </select>
                            <span className="il-sel-arrow">▾</span>
                          </div>
                        )}

                        {selectedUserObj ? (
                          <div className="il-user-prev">
                            <div className="il-avatar">{getInitials(selectedUserObj.userName)}</div>
                            <div>
                              <div className="il-uname">{selectedUserObj.userName}</div>
                              <div className="il-urole">
                                {selectedUserObj.userTypeId === 1 ? "Admin" :
                                 selectedUserObj.userTypeId === 2 ? "Manager" : "Agent"}
                              </div>
                            </div>
                            <div className="il-check">✓</div>
                          </div>
                        ) : (
                          !usersLoading && (
                            <div className="il-empty-user">
                              <div style={{ fontSize: 22, marginBottom: 4 }}>👥</div>
                              <div style={{ fontSize: 12, color: "#b0aac8", fontWeight: 600 }}>No user selected</div>
                              <div style={{ fontSize: 10, color: "#c8c4da", marginTop: 2 }}>Choose from dropdown above</div>
                            </div>
                          )
                        )}
                      </div>

                      {/* RIGHT — Upload File */}
                      <div>
                        <div className="il-section-label">Upload Excel File</div>

                        {!uploadDone ? (
                          <>
                            <div className="il-hint">
                              <span style={{ fontSize: 12 }}>💡</span>
                              <span className="il-hint-txt">
                                Need the format?{" "}
                                <button className="il-hint-link" onClick={handleDownloadTemplate}>
                                  Download template →
                                </button>
                              </span>
                            </div>

                            <div
                              className={`il-dz ${isDragging ? "dragging" : ""} ${uploadedFile ? "has-file" : ""}`}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                              onClick={() => !uploadedFile && fileInputRef.current?.click()}
                            >
                              <span className="il-dz-ico">{uploadedFile ? "📋" : "☁️"}</span>
                              <div className="il-dz-ttl">
                                {uploadedFile ? uploadedFile.name : "Drop your .xlsx file here"}
                              </div>
                              <div className="il-dz-sub">
                                {uploadedFile ? "File ready to import" : "or click to browse"}
                              </div>
                              {!uploadedFile && (
                                <>
                                  <button
                                    className="il-browse"
                                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                  >
                                    📁 Browse Files
                                  </button>
                                  <div><span className="il-fmt-tag">.xlsx only</span></div>
                                </>
                              )}
                            </div>

                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".xlsx"
                              style={{ display: "none" }}
                              onChange={handleFileSelect}
                            />

                            {uploadedFile && (
                              <div className="il-fstrip">
                                <div className="il-fico">📗</div>
                                <div>
                                  <div className="il-fname">{uploadedFile.name}</div>
                                  <div className="il-fsize">{formatFileSize(uploadedFile.size)}</div>
                                </div>
                                <button
                                  className="il-fremove"
                                  onClick={() => {
                                    setUploadedFile(null);
                                    if (fileInputRef.current) fileInputRef.current.value = "";
                                  }}
                                >✕</button>
                              </div>
                            )}

                            {isUploading && (
                              <div className="il-prog">
                                <div className="il-prog-row">
                                  <span className="il-prog-lbl">Importing leads...</span>
                                  <span className="il-prog-pct">{uploadProgress}%</span>
                                </div>
                                <div className="il-prog-track">
                                  <div className="il-prog-fill" style={{ width: `${uploadProgress}%` }} />
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          /* ── Success / Result panel ── */
                          <div className="il-success">
                            <div className="il-success-ring">✓</div>
                            <div className="il-success-ttl">Import Complete!</div>
                            <div className="il-success-sub">{uploadedFile?.name}</div>

                            {uploadResult && (
                              <>
                                {/* Stats */}
                                <div className="il-result-stats">
                                  <div className="il-stat il-stat-imported">
                                    ✅ {uploadResult.imported} Imported
                                  </div>
                                  {uploadResult.duplicates > 0 && (
                                    <div className="il-stat il-stat-duplicate">
                                      🔁 {uploadResult.duplicates} Duplicate{uploadResult.duplicates > 1 ? "s" : ""}
                                    </div>
                                  )}
                                  {uploadResult.skipped > 0 && (
                                    <div className="il-stat il-stat-skipped">
                                      ⚠️ {uploadResult.skipped} Skipped
                                    </div>
                                  )}
                                  {uploadResult.errorCount > 0 && (
                                    <div className="il-stat il-stat-errors">
                                      ❌ {uploadResult.errorCount} Error{uploadResult.errorCount > 1 ? "s" : ""}
                                    </div>
                                  )}
                                </div>

                                {/* Inline error list */}
                                {uploadResult.errors.length > 0 && (
                                  <div className="il-errors">
                                    {uploadResult.errors.map((e, i) => (
                                      <div key={i} className="il-error-item">• {e}</div>
                                    ))}
                                  </div>
                                )}

                                {/* Download report button */}
                                {uploadResult.rowResults?.length > 0 && (
                                  <div>
                                    <button className="il-btn-download" onClick={downloadReport}>
                                      📥 Download Report (.xlsx)
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="il-card-footer">
                    <button className="il-btn-reset" onClick={handleReset}>Reset</button>
                    {!uploadDone ? (
                      <button
                        className="il-btn-import"
                        disabled={!selectedUser || !uploadedFile || isUploading}
                        onClick={handleImport}
                      >
                        {isUploading ? "⏳ Importing..." : "Import Leads →"}
                      </button>
                    ) : (
                      <button className="il-btn-import" onClick={handleReset}>＋ Import More</button>
                    )}
                  </div>

                </div>
              </div>

            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ImportLead;